import { useRef, useState, type ReactNode } from 'react';
import { Callout, FileCard, ProgressBar, theme } from '@agentisai/design-system';
import { UPLOAD_ACCEPT, type ArtifactListItem } from '@agentisai/shared-types';
import { formatArtifactMeta } from '../lib/formatArtifactMeta';
import { useArtifacts } from '../queries/useArtifacts';
import { RailNewButton } from './RailNewButton';
import { RemoveArtifactDialog } from './RemoveArtifactDialog';

const mutedMessage = {
  padding: theme.space[4],
  color: theme.color.neutral.faint,
  fontSize: theme.typography.fontSize.bodySm,
  lineHeight: theme.typography.lineHeight.body,
} as const;

export interface FilesRailBodyProps {
  /** The selected workstream whose artifacts to list. The Files panel is mounted
   * only while a workstream is selected (WorkspaceShell / S08), so this is set. */
  readonly workstreamId: string;
  /** View a file — opens it in the centre pane (S11). Receives the opaque id;
   * omitted → the View action is inert. */
  readonly onView?: (artifactId: string) => void;
  /** The upload in flight (Drop 8 / Phase G), if any — the {@link ProgressBar}
   * renders above the list while set. `progress` is 0–100 (Phase 2's XHR
   * progress); absent while no upload is running. */
  readonly uploading?: { filename: string; progress: number } | null;
  /** The last upload's failure message (pre-flight rejection, or a Phase
   * 1/2/3 failure) — a quiet Callout above the list, mirroring the
   * load-failure alert below. */
  readonly uploadError?: string | null;
}

/**
 * Files rail panel body (Drop 6 / S09). Slots into `WorkspaceShell.filesPanel` and
 * lists the selected workstream's agent-produced artifacts as `FileCard`s
 * (filename + a derived `type · size · date` meta line). Sibling to
 * `WorkstreamsRailBody`: same loading / error / empty idiom; the query
 * (`useArtifacts`) is keyed by workstream so it refetches on selection change and
 * S10's delete can invalidate it. View / Remove land in S11 / S10.
 */
export function FilesRailBody({
  workstreamId,
  onView,
  uploading,
  uploadError,
}: FilesRailBodyProps) {
  const query = useArtifacts(workstreamId);
  // The file awaiting delete-confirmation (S10). Component-local + ephemeral —
  // one subtree owns it (frontend-architecture R1.0); the confirm Modal mounts
  // only while it is set. On CANCEL the DS `useDialog` cleanup restores focus to
  // the triggering card for free (it's still there); the SUCCESS path is handled
  // by `handleDeleted` below (the card is gone by then).
  const [pendingRemoval, setPendingRemoval] = useState<ArtifactListItem | null>(null);
  // A stable, always-rendered wrapper used as the focus landing after a delete.
  const bodyRef = useRef<HTMLDivElement>(null);

  function handleDeleted() {
    setPendingRemoval(null);
    // The removed card's Remove button unmounts on the refetch, so the confirm
    // dialog's focus-restore (useDialog) would drop focus to <body>. Move focus
    // into the Files panel body on the next frame — after the dialog's unmount +
    // restore commit — so a keyboard / screen-reader user keeps context instead
    // of being stranded at the top of the document.
    requestAnimationFrame(() => bodyRef.current?.focus());
  }

  let content: ReactNode;
  if (query.isLoading) {
    content = <div style={mutedMessage}>Loading files…</div>;
  } else if (query.isError) {
    content = (
      <div role="alert" style={mutedMessage}>
        Couldn't load files. We'll retry automatically.
      </div>
    );
  } else {
    // Newest first (R2 — derived at render). The agent lists ledger/append
    // order, so sort by `lastModified` desc here; spread first so we never
    // mutate the React Query cache's array in place.
    const items = [...(query.data?.data.artifacts ?? [])].sort(
      (a, b) => Date.parse(b.lastModified) - Date.parse(a.lastModified),
    );
    content =
      items.length === 0 ? (
        <div style={mutedMessage}>Files an agent creates in this workstream will appear here.</div>
      ) : (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: theme.space[2],
            padding: `0 ${theme.space[4]}`,
          }}
        >
          {items.map((item) => (
            <FileCard
              key={item.artifactId}
              filename={item.filename}
              meta={formatArtifactMeta(item)}
              onView={() => onView?.(item.artifactId)}
              onRemove={() => setPendingRemoval(item)}
            />
          ))}
        </div>
      );
  }

  return (
    <div ref={bodyRef} tabIndex={-1} style={{ outline: 'none' }}>
      {uploading && (
        <div style={{ padding: `${theme.space[3]} ${theme.space[4]} 0` }}>
          <div
            style={{
              fontSize: theme.typography.fontSize.bodySm,
              color: theme.color.neutral.muted,
              marginBottom: theme.space['1_5'],
            }}
          >
            Uploading {uploading.filename}…
          </div>
          <ProgressBar value={uploading.progress} aria-label={`Uploading ${uploading.filename}`} />
        </div>
      )}
      {uploadError && (
        <div role="alert" style={{ padding: `${theme.space[3]} ${theme.space[4]} 0` }}>
          <Callout tone="danger">{uploadError}</Callout>
        </div>
      )}
      {content}
      {pendingRemoval && (
        <RemoveArtifactDialog
          workstreamId={workstreamId}
          artifact={pendingRemoval}
          onClose={() => setPendingRemoval(null)}
          onDeleted={handleDeleted}
        />
      )}
    </div>
  );
}
FilesRailBody.displayName = 'FilesRailBody';

export interface NewFileActionProps {
  /** A file was picked (and passed `validateUpload`'s pre-flight check is the
   * caller's job, not this component's — it just relays the raw `File`). */
  readonly onSelectFile: (file: File) => void;
  /** Disable while an upload is already in flight (one at a time). */
  readonly disabled?: boolean;
}

/**
 * The Files rail's "+ New" subheader action (mirrors the Workstreams "+ New"
 * pill). Drop 8 / Phase G: a hidden native file input triggered by the
 * visible pill, `accept={UPLOAD_ACCEPT}` as a **picker hint only** — the real
 * gate is `validateUpload`, run by the caller regardless of how the file
 * arrived (design doc "the SPA check is not decoration"). The input value is
 * cleared after each change so re-picking the same filename still fires.
 */
export function NewFileAction({ onSelectFile, disabled = false }: NewFileActionProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={UPLOAD_ACCEPT}
        style={{ display: 'none' }}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) onSelectFile(file);
        }}
      />
      <RailNewButton onClick={() => inputRef.current?.click()} disabled={disabled}>
        + New
      </RailNewButton>
    </>
  );
}
NewFileAction.displayName = 'NewFileAction';
